/**
 * Markets Configuration Page
 *
 * Allows site owners to configure target markets for keyword research
 * and demand analysis. Each market has its own language, location,
 * currency, and demand thresholds.
 *
 * Date: 2026-01-26
 */

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Globe, Plus, Pencil, Trash2, Star, AlertCircle, Map, Users, Zap, ChevronRight, Check } from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import { marketsService, Market, RegionalTemplate, LanguageGroup, LOCATION_CODES, CURRENCIES, LANGUAGES } from '@/services/marketsService';

const LOCATIONS = Object.keys(LOCATION_CODES);

// Template icons and colors
const TEMPLATE_CONFIG: Record<string, { icon: React.ElementType; color: string; description: string }> = {
  western_europe: { icon: Globe, color: 'bg-blue-100 text-blue-600', description: 'Major Western European markets' },
  northern_europe: { icon: Globe, color: 'bg-cyan-100 text-cyan-600', description: 'Nordic countries' },
  central_europe: { icon: Globe, color: 'bg-indigo-100 text-indigo-600', description: 'German-speaking and Central European markets' },
  latin_america: { icon: Globe, color: 'bg-orange-100 text-orange-600', description: 'Spanish and Portuguese speaking Americas' },
  north_america: { icon: Globe, color: 'bg-green-100 text-green-600', description: 'US, Canada, and Mexico' },
  asia_pacific: { icon: Globe, color: 'bg-pink-100 text-pink-600', description: 'Major Asian and Pacific markets' },
  middle_east: { icon: Globe, color: 'bg-amber-100 text-amber-600', description: 'Arabic-speaking Gulf and Middle East' },
};

export default function MarketsConfiguration() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const [markets, setMarkets] = useState<Market[]>([]);
  const [templates, setTemplates] = useState<RegionalTemplate[]>([]);
  const [languageGroups, setLanguageGroups] = useState<LanguageGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<RegionalTemplate | null>(null);
  const [editingMarket, setEditingMarket] = useState<Partial<Market> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    language: 'es',
    location: 'Spain',
    currency: 'EUR',
    isPrimary: false,
    highDemandThreshold: 5000,
    lowDemandThreshold: 500,
    exchangeRateToPrimary: 1,
  });

  useEffect(() => {
    if (selectedSite?.id) {
      loadMarkets();
      loadTemplates();
    }
  }, [selectedSite?.id]);

  const loadMarkets = async () => {
    if (!selectedSite?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await marketsService.getSiteMarkets(selectedSite.id);
      setMarkets(data);
    } catch (err) {
      setError('Failed to load markets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    console.log('[MarketsConfig] Loading templates...');
    try {
      const [templatesData, groupsData] = await Promise.all([
        marketsService.getRegionalTemplates(),
        marketsService.getLanguageGroups(),
      ]);
      console.log('[MarketsConfig] Templates loaded:', templatesData?.length, 'groups:', groupsData?.length);
      setTemplates(templatesData || []);
      setLanguageGroups(groupsData || []);
    } catch (err) {
      console.error('[MarketsConfig] Failed to load templates:', err);
      // Don't block the page if templates fail to load
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedSite?.id || !selectedTemplate) return;
    setApplyingTemplate(true);
    try {
      const result = await marketsService.addMarketsFromTemplate(
        selectedSite.id,
        selectedTemplate.id
      );
      toast({
        title: 'Template applied',
        description: `Added ${result.added} markets from "${selectedTemplate.name}" template.`,
      });
      await loadMarkets();
      setTemplateDialogOpen(false);
      setSelectedTemplate(null);
    } catch (err) {
      toast({
        title: 'Failed to apply template',
        description: 'Could not add markets from template. Please try again.',
        variant: 'destructive',
      });
      console.error(err);
    } finally {
      setApplyingTemplate(false);
    }
  };

  const handleOpenDialog = (market?: Market) => {
    if (market) {
      setEditingMarket(market);
      setFormData({
        language: market.language,
        location: market.location,
        currency: market.currency,
        isPrimary: market.isPrimary,
        highDemandThreshold: market.highDemandThreshold,
        lowDemandThreshold: market.lowDemandThreshold,
        exchangeRateToPrimary: market.exchangeRateToPrimary,
      });
    } else {
      setEditingMarket(null);
      setFormData({
        language: 'es',
        location: 'Spain',
        currency: 'EUR',
        isPrimary: markets.length === 0, // First market is primary by default
        highDemandThreshold: 5000,
        lowDemandThreshold: 500,
        exchangeRateToPrimary: 1,
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedSite?.id) return;
    setSaving(true);
    setError(null);
    try {
      await marketsService.saveMarket(selectedSite.id, {
        ...formData,
        id: editingMarket?.id,
        locationCode: LOCATION_CODES[formData.location],
      });
      await loadMarkets();
      setDialogOpen(false);
    } catch (err) {
      setError('Failed to save market');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (marketId: string) => {
    if (!confirm('Are you sure you want to delete this market?')) return;
    try {
      await marketsService.deleteMarket(marketId);
      await loadMarkets();
    } catch (err) {
      setError('Failed to delete market');
      console.error(err);
    }
  };

  const handleSetPrimary = async (market: Market) => {
    if (!selectedSite?.id || market.isPrimary) return;
    try {
      await marketsService.saveMarket(selectedSite.id, {
        ...market,
        isPrimary: true,
      });
      await loadMarkets();
    } catch (err) {
      setError('Failed to set primary market');
      console.error(err);
    }
  };

  const primaryMarket = markets.find(m => m.isPrimary);

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="h-5 w-5" />
              <span>Please select a site to configure markets.</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Markets Configuration</h1>
          <p className="text-muted-foreground">
            Configure target markets for keyword research and demand analysis
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Market
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Primary Market Card */}
      {primaryMarket && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
              Primary Market
            </CardTitle>
            <CardDescription>
              The primary market is used as the default for demand analysis and currency conversion.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-muted-foreground text-xs">Location</Label>
                <p className="font-medium">{primaryMarket.location}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Language</Label>
                <p className="font-medium">
                  {LANGUAGES.find(l => l.code === primaryMarket.language)?.name || primaryMarket.language}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Currency</Label>
                <p className="font-medium">{primaryMarket.currency}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">High Demand Threshold</Label>
                <p className="font-medium">
                  {primaryMarket.highDemandThreshold.toLocaleString()} {primaryMarket.currency}/mo
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regional Templates */}
      {templates.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Map className="h-5 w-5" />
              Quick Setup with Regional Templates
            </CardTitle>
            <CardDescription>
              Add multiple markets at once using pre-configured regional templates.
              Templates use grouped enrichment for efficient keyword data fetching.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {templates.map((template) => {
                const config = TEMPLATE_CONFIG[template.id] || { icon: Globe, color: 'bg-gray-100 text-gray-600', description: '' };
                const Icon = config.icon;
                return (
                  <button
                    key={template.id}
                    onClick={() => {
                      setSelectedTemplate(template);
                      setTemplateDialogOpen(true);
                    }}
                    className="flex flex-col items-start p-4 rounded-lg border hover:border-primary hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className={`p-2 rounded-lg ${config.color} mb-3`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h4 className="font-medium">{template.name}</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {template.marketCount} markets
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {config.description}
                    </p>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-700 dark:text-blue-400">Efficient Multi-Market Enrichment</p>
                  <p className="text-blue-600 dark:text-blue-300 text-xs mt-1">
                    When you have multiple markets in the same language, PageMind queries Google Ads only once
                    per language (using the representative market) and applies regional multipliers.
                    This reduces API calls by up to 90%.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Markets Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            All Markets
          </CardTitle>
          <CardDescription>
            Configure multiple markets to aggregate keyword data across regions.
            Each market fetches CPC and search volume from Google Ads for its specific location.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading markets...</div>
          ) : markets.length === 0 ? (
            <div className="text-center py-8">
              <Globe className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No markets configured yet.</p>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Market
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location</TableHead>
                  <TableHead>Language</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead className="text-right">High Threshold</TableHead>
                  <TableHead className="text-right">Low Threshold</TableHead>
                  <TableHead className="text-right">Exchange Rate</TableHead>
                  <TableHead className="text-center">Primary</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {markets.map((market) => (
                  <TableRow key={market.id}>
                    <TableCell className="font-medium">{market.location}</TableCell>
                    <TableCell>
                      {LANGUAGES.find(l => l.code === market.language)?.name || market.language}
                    </TableCell>
                    <TableCell>{market.currency}</TableCell>
                    <TableCell className="text-right">
                      {market.highDemandThreshold.toLocaleString()} {market.currency}
                    </TableCell>
                    <TableCell className="text-right">
                      {market.lowDemandThreshold.toLocaleString()} {market.currency}
                    </TableCell>
                    <TableCell className="text-right">
                      {market.isPrimary ? '1.0' : market.exchangeRateToPrimary.toFixed(4)}
                    </TableCell>
                    <TableCell className="text-center">
                      {market.isPrimary ? (
                        <Badge variant="default" className="bg-yellow-500 hover:bg-yellow-600">
                          <Star className="h-3 w-3 mr-1" />
                          Primary
                        </Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetPrimary(market)}
                          title="Set as primary"
                        >
                          Set Primary
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(market)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {!market.isPrimary && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(market.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>How Markets Work</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>
            <strong>Keyword Enrichment:</strong> For each configured market, PageMind fetches
            CPC (Cost Per Click) and monthly search volume from Google Ads Keyword Planner.
            This data is used to calculate the search value of your product categories.
          </p>
          <p>
            <strong>Currency Conversion:</strong> Values from secondary markets are converted
            to the primary market's currency using the exchange rate you configure.
            This allows for aggregated value reporting across all markets.
          </p>
          <p>
            <strong>Demand Thresholds:</strong> Categories are classified based on their
            monthly search value (CPC x Search Volume):
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>High Demand:</strong> Above the high threshold (e.g., {'>'}5,000/mo)</li>
            <li><strong>Medium Demand:</strong> Between low and high thresholds</li>
            <li><strong>Low Demand:</strong> Below the low threshold (e.g., {'<'}500/mo)</li>
          </ul>
        </CardContent>
      </Card>

      {/* Add/Edit Market Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingMarket ? 'Edit Market' : 'Add Market'}
            </DialogTitle>
            <DialogDescription>
              Configure a target market for keyword research.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Select
                  value={formData.location}
                  onValueChange={(value) => setFormData({ ...formData, location: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map((loc) => (
                      <SelectItem key={loc} value={loc}>
                        {loc}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="language">Language</Label>
                <Select
                  value={formData.language}
                  onValueChange={(value) => setFormData({ ...formData, language: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((curr) => (
                      <SelectItem key={curr} value={curr}>
                        {curr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="exchangeRate">Exchange Rate to Primary</Label>
                <Input
                  id="exchangeRate"
                  type="number"
                  step="0.0001"
                  value={formData.exchangeRateToPrimary}
                  onChange={(e) => setFormData({
                    ...formData,
                    exchangeRateToPrimary: parseFloat(e.target.value) || 1,
                  })}
                  disabled={formData.isPrimary || markets.length === 0}
                />
                <p className="text-xs text-muted-foreground">
                  1 {formData.currency} = {formData.exchangeRateToPrimary} {primaryMarket?.currency || 'EUR'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="highThreshold">High Demand Threshold</Label>
                <Input
                  id="highThreshold"
                  type="number"
                  value={formData.highDemandThreshold}
                  onChange={(e) => setFormData({
                    ...formData,
                    highDemandThreshold: parseInt(e.target.value) || 5000,
                  })}
                />
                <p className="text-xs text-muted-foreground">{formData.currency}/month</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lowThreshold">Low Demand Threshold</Label>
                <Input
                  id="lowThreshold"
                  type="number"
                  value={formData.lowDemandThreshold}
                  onChange={(e) => setFormData({
                    ...formData,
                    lowDemandThreshold: parseInt(e.target.value) || 500,
                  })}
                />
                <p className="text-xs text-muted-foreground">{formData.currency}/month</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isPrimary"
                checked={formData.isPrimary}
                onCheckedChange={(checked) => setFormData({ ...formData, isPrimary: checked })}
                disabled={editingMarket?.isPrimary}
              />
              <Label htmlFor="isPrimary">Set as primary market</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Market'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Map className="h-5 w-5" />
              Apply {selectedTemplate?.name} Template
            </DialogTitle>
            <DialogDescription>
              This will add the following {selectedTemplate?.marketCount} markets to your site.
              Existing markets with the same language/location will be updated.
            </DialogDescription>
          </DialogHeader>

          {selectedTemplate && (
            <div className="py-4">
              <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Location</TableHead>
                      <TableHead>Language</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead className="text-right">Multiplier</TableHead>
                      <TableHead className="text-center">Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedTemplate.markets.map((market, idx) => (
                      <TableRow key={`${market.language}-${market.location}-${idx}`}>
                        <TableCell className="font-medium">{market.location}</TableCell>
                        <TableCell>
                          {LANGUAGES.find(l => l.code === market.language)?.name || market.language}
                        </TableCell>
                        <TableCell>{market.currency}</TableCell>
                        <TableCell className="text-right font-mono">
                          {market.multiplier.toFixed(2)}x
                        </TableCell>
                        <TableCell className="text-center">
                          {market.isRepresentative ? (
                            <Badge variant="default" className="bg-blue-500">
                              <Users className="h-3 w-3 mr-1" />
                              Representative
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              Estimated
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                <div className="flex items-start gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-700 dark:text-amber-400">
                      Representative vs Estimated Markets
                    </p>
                    <p className="text-amber-600 dark:text-amber-300 text-xs mt-1">
                      <strong>Representative</strong> markets are queried directly from Google Ads.
                      <strong> Estimated</strong> markets use the representative market's data multiplied
                      by a regional factor based on market size.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setTemplateDialogOpen(false);
              setSelectedTemplate(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleApplyTemplate} disabled={applyingTemplate}>
              {applyingTemplate ? (
                'Applying...'
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Apply Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
